import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { usePersistentState } from 'hooks/useStorage/useStorage';
import { useDispatch, useSelector } from 'react-redux';
import { addToSearchCache, isCacheValid, clearSearchCache, hasCacheExpired } from 'providers/reducers/search-reducer';
import { RootState, AppDispatch } from 'providers/reducers';
import GSApi from 'api/Api';
import { useNavigation } from 'hooks/useNavigation';
import { StaticPageRoutes } from 'types/RouteTypes';
import { FetchResponse } from 'types/ApiTypes';
import { SearchData, SearchDatasArray, DEFAULT_TYPE_MAP, EMPTY_SEARCH_RESULT } from 'types/Search';
import { SearchSectionType, findSearchResponseWithType } from 'utils/searchUtils';
import { PageData } from 'types/Page';
import useYouboraSearchTracking from 'hooks/useYouboraSearchTracking';
import { useAuth } from 'providers/useAuth/AuthContext';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { guardedSessionStorage } from 'utils/fnStorage';

export const QUERY_STRING = 'term';

const useSearchService = () => {
    const { navigate, location } = useNavigation();
    const { isLoggedIn } = useAuth();
    const { config } = useConfig();
    const [searchTerm, setSearchTerm] = useState<string>(null);
    const [searchData, setSearchData] = useState<SearchDatasArray>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [activeSearchResponse, setActiveSearchResponse] = useState<SearchData>(EMPTY_SEARCH_RESULT);
    const [collectionsList, setCollectionsList] = useState<Array<{ id: string; type: SearchSectionType; title?: string }>>([]);

    const { store: searchHistory, set: setSearchHistory } = usePersistentState('searchHistory', []);

    const dispatch = useDispatch<AppDispatch>();
    const searchCache = useSelector((state: RootState) => state.searchStore.cache);
    const searchPageId = config?.api_config?.routes?.Zattoo?.search_page_id;

    const { trackSearchResult } = useYouboraSearchTracking();

    const searchRequestRef = useRef(0);

    const [searchResults, setSearchResults] = useState({
        movies: 0,
        series: 0,
        total: 0,
    });

    const fetchCollectionSearchResults = useCallback(
        async (searchInput: string, collections: Array<{ id: string; type: SearchSectionType }>): Promise<SearchDatasArray> => {
            const cachedEntry = searchCache[searchInput];

            if (cachedEntry && isCacheValid(cachedEntry.timestamp)) {
                return cachedEntry.data;
            }

            const searchPromises = collections.map((collection) => {
                const queryParams = {
                    per_page: 20,
                    page: 1,
                    query_string: searchInput,
                };

                return GSApi.getCollection(collection.id, queryParams)
                    .then((response) => {
                        if (response.error) {
                            console.error(`Error fetching collection ${collection.id}:`, response.error);
                            return null;
                        }

                        const collectionData = response.response as SearchData;

                        if (collectionData?.results) {
                            collectionData.results.forEach((item) => {
                                if (!item.type) item.type = collection.type;
                            });
                        }

                        return collectionData;
                    })
                    .catch((error) => {
                        console.error(`Error searching collection ${collection.id}:`, error);
                        return null;
                    });
            });

            const results = await Promise.all(searchPromises);
            const filteredResults = results.filter(Boolean) as SearchDatasArray;

            // A failed collection caches an incomplete result set for CACHE_DURATION,
            // so only cache when every collection responded
            if (filteredResults.length > 0 && filteredResults.length === collections.length) {
                dispatch(addToSearchCache(searchInput, filteredResults));
            }

            return filteredResults;
        },
        [searchCache, dispatch]
    );

    const handleSearchHistory = useCallback(
        (query: string) => {
            if (!query || query.length < 3) return;

            if (searchHistory?.find((item) => item === query)) {
                const filteredSearchHistory = searchHistory.filter((item) => item !== query);
                filteredSearchHistory.unshift(query);
                setSearchHistory(filteredSearchHistory);
            } else {
                const searchHistoryToSet = [query, ...searchHistory];
                if (searchHistoryToSet.length > 15) {
                    searchHistoryToSet.pop();
                }
                setSearchHistory(searchHistoryToSet);
            }
        },
        [searchHistory, setSearchHistory]
    );

    const deleteSearchHistoryItem = useCallback(
        (value: string) => {
            const filteredSearchHistory = searchHistory.filter((item) => item !== value);
            setSearchHistory(filteredSearchHistory);
        },
        [searchHistory, setSearchHistory]
    );

    const updateSearchResults = (movies: number, series: number, total: number) => {
        const totalResults = total || movies + series;
        setSearchResults({
            movies,
            series,
            total: totalResults,
        });

        if (searchTerm) {
            trackSearchResult(totalResults, searchTerm);
        }
    };

    const doSearch = useCallback(
        async (query: string): Promise<SearchDatasArray> => {
            const searchInput = query?.trim() ?? '';

            if (searchInput.length < 3) {
                searchRequestRef.current += 1;
                setSearchTerm(searchInput);
                setSearchData([]);
                setActiveSearchResponse(EMPTY_SEARCH_RESULT);
                setIsLoading(false);
                return [];
            }

            if (!collectionsList.length) {
                setSearchData([]);
                setIsLoading(true);
                return [];
            }

            const requestId = searchRequestRef.current + 1;
            searchRequestRef.current = requestId;

            setSearchData([]);
            setIsLoading(true);
            handleSearchHistory(searchInput);
            setSearchTerm(searchInput);

            try {
                const responses = await fetchCollectionSearchResults(searchInput, collectionsList);

                if (requestId !== searchRequestRef.current) {
                    return responses;
                }

                setSearchData(responses);
                setIsLoading(false);
                return responses;
            } catch (error) {
                console.error('Search error:', error);

                if (requestId === searchRequestRef.current) {
                    setSearchData([]);
                    setIsLoading(false);
                }
                return [];
            }
        },
        [collectionsList, handleSearchHistory, fetchCollectionSearchResults]
    );

    const initiateSearch = useCallback(
        async (query: string): Promise<SearchDatasArray> => {
            const searchInput = query?.trim() ?? '';

            const urlQueryParams = new URLSearchParams(window.location.search);
            const currentQueryParam = urlQueryParams.get(QUERY_STRING);

            if (currentQueryParam !== null && currentQueryParam !== searchInput) {
                if (searchInput) {
                    urlQueryParams.set(QUERY_STRING, searchInput);
                } else {
                    urlQueryParams.delete(QUERY_STRING);
                }
                navigate({ search: urlQueryParams.toString() }, { replace: true });
            } else if (currentQueryParam === null && searchInput.length > 2) {
                urlQueryParams.set(QUERY_STRING, searchInput);
                navigate({ search: urlQueryParams.toString() }, { replace: true });
            }

            return doSearch(searchInput);
        },
        [navigate, doSearch]
    );

    const exitSearch = useCallback(
        (exit: boolean) => {
            searchRequestRef.current += 1;
            setSearchTerm(null);
            setSearchData([]);
            setActiveSearchResponse(EMPTY_SEARCH_RESULT);

            if (exit) {
                navigate(guardedSessionStorage.getItem('goto') || '/');
            }
        },
        [navigate]
    );

    const enterSearch = useCallback(
        (query?: string) => {
            if (query?.trim()?.length < 3) return;

            guardedSessionStorage.setItem('goto', location.pathname);

            const searchPath = query?.trim()
                ? `${StaticPageRoutes.SEARCH}?${QUERY_STRING}=${encodeURIComponent(query.trim())}`
                : StaticPageRoutes.SEARCH;

            navigate(searchPath);
        },
        [location.pathname, navigate]
    );

    const getSearchResponseByType = useCallback(
        (type: SearchSectionType): SearchData => {
            const response = findSearchResponseWithType(searchData, type);
            return response || EMPTY_SEARCH_RESULT;
        },
        [searchData]
    );

    useEffect(() => {
        const loadSearchPage = async () => {
            try {
                if (!isLoggedIn || !config) return;

                if (!searchPageId) return;

                const response: FetchResponse<PageData> = await GSApi.fetchPage(searchPageId);

                if (response.error) {
                    console.error('Error fetching search page:', response.error);
                    return;
                }

                const pageData = response.response;

                if (pageData && pageData.pageSections) {
                    const collectionIds = pageData.pageSections
                        .filter((section) => section.collectionId)
                        .map((section) => ({
                            id: section.collectionId,
                            title: section.title,
                            type: DEFAULT_TYPE_MAP[section.title] || 'VOD_ASSET',
                        }));

                    setCollectionsList(collectionIds);
                }
            } catch (error) {
                console.error('Error loading search page:', error);
            }
        };

        loadSearchPage();
    }, [searchPageId, isLoggedIn, config]);

    useEffect(() => {
        const urlQueryParams = new URLSearchParams(location.search);
        const term = urlQueryParams.get(QUERY_STRING)?.trim() ?? null;

        if (term != null && term !== searchTerm) {
            doSearch(term);
        }
    }, [location.search, doSearch, searchTerm]);

    return {
        searchTerm,
        isLoading,
        search: initiateSearch,
        doSearch,
        exitSearch,
        enterSearch,
        updateSearchResults,
        searchResults,
        searchHistory,
        searchData,
        activeSearchResponse,
        getSearchResponseByType,
        handleSearchHistory,
        deleteSearchHistoryItem,
    };
};

export const SearchContext = createContext<ReturnType<typeof useSearchService>>(null);

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const dispatch = useDispatch<AppDispatch>();
    const cache = useSelector((state: RootState) => state.searchStore.cache);

    useEffect(() => {
        const checkCacheValidity = () => {
            if (hasCacheExpired(cache)) {
                dispatch(clearSearchCache());
            }
        };

        checkCacheValidity();

        const intervalId = setInterval(checkCacheValidity, 60 * 60 * 1000);

        return () => clearInterval(intervalId);
    }, [dispatch, cache]);

    return <SearchContext.Provider value={useSearchService()}>{children}</SearchContext.Provider>;
};

export const useSearch = () => useContext(SearchContext);
